// Display helpers for Prepare > Automix. Pure — see __tests__/automixFormat.test.js.

// The engine's transition templates (scripts/djset/strategies.py TEMPLATES) in
// a DJ's words: the name on the chip and what it sounds like.
const TRANSITIONS = {
    long_blend: { label: 'Long blend', hint: 'Both tracks ride together, the bass swaps on the phrase' },
    filter_swap: { label: 'Filter swap', hint: 'The incoming track opens up through the filter, then takes the low end' },
    percussion_tease: { label: 'Percussion tease', hint: 'The next track’s hats and percussion play over the top before it lands' },
    echo_out: { label: 'Echo out', hint: 'The outgoing track is thrown into an echo as the next one drops' },
    breakdown_steal: { label: 'Breakdown steal', hint: 'The next groove slides in under the breakdown and takes over at the resolution' },
    loop_roll_drop: { label: 'Loop roll', hint: 'A shrinking loop builds tension and slams into the next track’s drop' },
    intro_reveal: { label: 'Intro reveal', hint: 'The next track’s intro plays underneath and is revealed when its bass comes in' },
    double_drop: { label: 'Double drop', hint: 'Both tracks drop together' },
    hard_cut: { label: 'Cut', hint: 'A straight cut on the one' },
    brake_cut: { label: 'Brake', hint: 'The outgoing track brakes to a stop into the next one' },
    backspin_cut: { label: 'Backspin', hint: 'A backspin out of the outgoing track' },
};

export function transitionLabel(strategy) {
    if (TRANSITIONS[strategy]) return TRANSITIONS[strategy].label;
    const words = String(strategy || '').replace(/_/g, ' ').trim();
    return words ? words.charAt(0).toUpperCase() + words.slice(1) : 'Transition';
}

export function transitionHint(strategy) {
    return TRANSITIONS[strategy]?.hint || '';
}

// A set is a night's worth of records, not a library. Raised 40 → 60
// (2026-09-18, Erez: "let them do what they want"): the engine scores every
// pair in parallel now, and the build shows its own time and disk estimate —
// the cap only keeps a whole library from landing in one set by accident.
export const AUTOMIX_MAX_TRACKS = 60;

/**
 * Which playlist rows go into the set: the ones with an audio file on this
 * machine, up to the limit, in playlist order.
 * @returns {{included: object[], overLimit: number}}
 */
export function automixIncluded(rows) {
    const usable = (rows || []).filter((r) => r.path);
    return { included: usable.slice(0, AUTOMIX_MAX_TRACKS), overLimit: Math.max(0, usable.length - AUTOMIX_MAX_TRACKS) };
}

/** 75 → "1:15", 3944 → "1:05:44". */
export function formatClock(sec) {
    if (!(Number(sec) >= 0)) return '–:––';
    const s = Math.floor(sec);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const ss = String(s % 60).padStart(2, '0');
    return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`;
}

/** Length in words for summaries: 3944 → "1 h 6 min", 1500 → "25 min". */
export function formatLength(sec) {
    if (!(Number(sec) > 0)) return '';
    const min = Math.round(sec / 60);
    if (min < 60) return `${Math.max(1, min)} min`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m ? `${h} h ${m} min` : `${h} h`;
}

export function formatBytes(bytes) {
    if (!(Number(bytes) >= 0)) return '';
    const gb = bytes / 1e9;
    if (gb >= 10) return `${Math.round(gb)} GB`;
    if (gb >= 1) return `${gb.toFixed(1)} GB`;
    return `${Math.max(1, Math.round(bytes / 1e6))} MB`;
}

/** "120–126 BPM" across a list of rows; '' when nothing is known. */
export function bpmRange(rows) {
    const bpms = (rows || []).map((r) => Number(r.bpm)).filter((b) => b > 0);
    if (!bpms.length) return '';
    const lo = Math.round(Math.min(...bpms));
    const hi = Math.round(Math.max(...bpms));
    return lo === hi ? `${lo} BPM` : `${lo}–${hi} BPM`;
}

/** Shorten a home-relative path for display: /Users/x/Music/Sets → ~/Music/Sets. */
export function displayPath(p) {
    if (!p) return '';
    return p.replace(/^\/Users\/[^/]+/, '~').replace(/^[A-Za-z]:\\Users\\[^\\]+/, '~');
}

/**
 * Rough build time in seconds from what is already cached. Per record,
 * measured on an M1 Pro: stems ≈ 45 s, analysis ≈ 6 s, and the planning +
 * beatmatch + render + master pass ≈ 30 s (same figures as the progress bar's
 * stage weights, src/backend/automix/automixProgress.js).
 */
export function estimateBuildSec({ present = 0, stemsCached = 0, analysisCached = 0, withStems = false }) {
    if (!present) return 0;
    const stems = withStems ? Math.max(0, present - stemsCached) * 45 : 0;
    // fresh stems change the analysis cache key, so those records are re-analysed
    const toAnalyse = withStems ? Math.max(present - analysisCached, present - stemsCached) : present - analysisCached;
    return stems + Math.max(0, toAnalyse) * 6 + present * 30 + 30;
}

/**
 * The set as timeline segments: one block per track from its start to the
 * next track's start, with the overlap where the next track is already
 * audible. All in seconds, clamped to the set length.
 */
export function timelineSegments(set) {
    const total = Number(set?.durationSec) || 0;
    const tracks = set?.tracks || [];
    if (!total || !tracks.length) return [];
    return tracks.map((t, i) => {
        const start = Math.max(0, Number(t.startSec) || 0);
        const next = tracks[i + 1];
        const end = next && Number(next.startSec) > start ? Math.min(total, Number(next.startSec)) : total;
        const tr = (set.transitions || []).find((x) => x.index === i);
        return {
            idx: i,
            start,
            end,
            // where the blend into the NEXT track begins inside this block
            blendStart: tr && Number(tr.startSec) > start ? Math.min(end, Number(tr.startSec)) : null,
            transition: tr || null,
            track: t,
        };
    });
}

/** Index of the track playing at `sec` (the last one whose start is ≤ sec). */
export function trackIndexAt(set, sec) {
    const tracks = set?.tracks || [];
    let at = 0;
    for (let i = 0; i < tracks.length; i++) {
        if ((Number(tracks[i].startSec) || 0) <= sec + 0.05) at = i;
    }
    return at;
}
